import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useExchangeRates } from "../../hooks/useExchangeRates";

const navLinks = [
  { label: "Anasayfa", path: "/" },
  { label: "Hakkımızda", path: "/hakkimizda" },
  { label: "Hizmetler", path: "/hizmetler" },
  { label: "Döviz Kurları", path: "/doviz-kurlari" },
  { label: "Hesaplama", path: "/hesaplama" },
  { label: "Galeri", path: "/galeri" },
] as const;

const TICKER_CODES = ["USD", "EUR", "GBP", "CHF", "GAU", "CEY", "XAG", "JPY"];

function RatesTicker() {
  const { rates } = useExchangeRates(120_000);
  if (rates.length === 0) return null;

  const keyRates = TICKER_CODES.map(code => rates.find(r => r.code === code)).filter(Boolean);
  const tickerRates = [...keyRates, ...keyRates];

  return (
    <div className="overflow-hidden bg-dark-200/80 border-b border-white/5">
      <div className="flex animate-[ticker_40s_linear_infinite] hover:[animation-play-state:paused] whitespace-nowrap">
        {tickerRates.map((r, i) => (
          <div
            key={`${r.code}-${i}`}
            className="inline-flex items-center gap-2 px-5 py-1.5 text-[11px] font-montserrat flex-shrink-0"
          >
            <span className="text-sm leading-none">{r.flag}</span>
            <span className="text-white/60 font-semibold">{r.code}</span>
            <span className="text-white/40">{r.buy}</span>
            <span className={`font-semibold ${r.up ? "text-green-400" : "text-red-400"}`}>
              {r.up ? "▲" : "▼"}{r.change}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 overflow-hidden">
      {/* Ticker - hide on scroll */}
      <div
        className={`transition-all duration-300 overflow-hidden ${
          scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"
        }`}
      >
        <RatesTicker />
      </div>

      {/* Nav bar */}
      <div
        className={`transition-all duration-300 ${
          scrolled || menuOpen
            ? "bg-black/95 backdrop-blur-md shadow-lg shadow-black/20"
            : "bg-black/40 backdrop-blur-sm"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/images/logo.webp"
              alt="BEY Döviz Logo"
              width={48}
              height={48}
              className="h-9 lg:h-12 w-auto object-contain"
              loading="eager"
            />
          </Link>

          {/* Desktop Nav */}
          <ul className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`relative font-montserrat text-sm font-medium tracking-wide transition-smooth whitespace-nowrap cursor-pointer ${
                    location.pathname === link.path
                      ? "text-gold"
                      : "text-white/80 hover:text-gold"
                  }`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gold rounded-full"
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <Link
            to="/doviz-kurlari"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 gold-gradient text-black text-sm font-semibold rounded-full whitespace-nowrap gold-glow cursor-pointer shadow-[0_0_12px_rgba(212,175,55,0.15)]"
          >
            <i className="ri-line-chart-line text-sm"></i>
            Güncel Kurlar
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Menü"
          >
            <i className={`text-xl transition-smooth ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="lg:hidden bg-dark-100/98 backdrop-blur-md border-t border-gold/10 overflow-hidden"
          >
            <ul className="px-6 py-6 flex flex-col gap-2">
              {navLinks.map((link, i) => (
                <motion.li
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                >
                  <Link
                    to={link.path}
                    className={`block font-montserrat text-base font-medium py-3 px-4 rounded-xl transition-smooth cursor-pointer ${
                      location.pathname === link.path
                        ? "text-gold bg-gold/5"
                        : "text-white/80 hover:text-gold hover:bg-white/[0.02]"
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navLinks.length * 0.05, duration: 0.3 }}
                className="mt-2"
              >
                <Link
                  to="/doviz-kurlari"
                  className="inline-flex items-center gap-2 px-5 py-3 gold-gradient text-black text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-line-chart-line"></i>
                  Güncel Kurlar
                </Link>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
